import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import MUIDataTable from 'mui-datatables';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';

import CustomToolbar from 'components/CustomToolbar/CustomToolbar';
import ReceiveProductsModal from 'components/PurchaseOrders/ReceiveProductsModal';
import PurchaseOrderPaymentModal from 'components/PurchaseOrders/PurchaseOrderPaymentModal';

import { updatePurchaseOrderStatus, deletePurchaseOrder, setNewPOStatus, setNewPOInfo, clearNewPO } from 'redux/actions';
import { Approved } from 'variables/status';

const PurchaseOrdersTable = (props) => {

  const [rowsSelected, setRowsSelected] = useState([]);
  const [rowsExpanded, setRowsExpanded] = useState([]);

  const [openReceiveModal, setOpenReceiveModal] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);

  const tableData = useMemo(() => {
    const initialTableData = [];

    for ( let i = 0; i < props.purchaseOrders.length; i++ ) {
      const purchaseOrder = props.purchaseOrders[i];
      const purchaseOrderVendor = props.vendors.find(vendor => vendor.id === purchaseOrder.vendorId);
      initialTableData.push({
        id: purchaseOrder.id,
        displayId: purchaseOrder.displayId,
        name: purchaseOrderVendor.name,
        endDate: moment(purchaseOrder.endDate).format('DD/MM/YYYY'),
        products: purchaseOrder.products,
        total: purchaseOrder.total,
        status: purchaseOrder.status,
        received: purchaseOrder.received ? "Yes" : "No"
      });
    }

    return initialTableData;

  }, [props.purchaseOrders, props.vendors]);

  const onRowSelectionChange = (curr, all, rowsSelected) => {
    setRowsSelected(rowsSelected);
  };

  const onRowExpansionChange = (currentRowsExpanded, allRowsExpanded) => {
    setRowsExpanded(allRowsExpanded.map(row => row.dataIndex));
  };

  const customToolbarSelect = (selectedRows) => {
    return(
      <CustomToolbar
        switchy={1}
        approvePurchaseOrder={approvePurchaseOrder}
        handleModalOpen={handleCancelModalOpen}
        receiveProductsOrPayment={receiveProductsOrPayment}
        status={props.purchaseOrders[selectedRows.data[0].dataIndex].status}
        received={props.purchaseOrders[selectedRows.data[0].dataIndex].received}
      />
    )
  };

  const receiveProductsOrPayment = (receiveProducts) => {
    if(receiveProducts) setOpenReceiveModal(true);
    else setOpenPaymentModal(true);
    
    const purchaseOrderInfo = props.purchaseOrders[rowsSelected[0]];
    const purchaseOrderProducts = purchaseOrderInfo.products.map(purchaseOrderProduct => {
      const productInfo = props.products.find(product => product.id === purchaseOrderProduct.productId);
      return {
        displayId: productInfo.displayId,
        name: productInfo.name,
        company: productInfo.company,
        unitCost: productInfo.unitCost,
        ...purchaseOrderProduct
      }
    });
    props.setNewPOInfo({
      displayId: purchaseOrderInfo.displayId,
      vendorId: purchaseOrderInfo.vendorId,
      poProducts: purchaseOrderProducts,
      startDate: purchaseOrderInfo.startDate,
      endDate: purchaseOrderInfo.endDate,
      status: purchaseOrderInfo.status,
      total: purchaseOrderInfo.total,
      payments: purchaseOrderInfo.payments,
      received: purchaseOrderInfo.received
    });
  };
  
  const approvePurchaseOrder = () => {
    const purchaseOrderInfo = props.purchaseOrders[rowsSelected[0]];

    const updates = {
      vendorId: purchaseOrderInfo.vendorId,
      incrementAmount: purchaseOrderInfo.total
    };
    props.updatePurchaseOrderStatus({ id: purchaseOrderInfo.id, status: Approved }, updates);
    
    receiveProductsOrPayment(false);
    props.setNewPOStatus(Approved);
  };

  const columns = [
    { label: "ID", name: "id", options: { display: 'excluded', filter: false, sort: false, viewColumns: false } },
    { label: "Order ID", name: "displayId", options: { filter: false, sort: false, viewColumns: false } },
    { label: "Vendor Name", name: "name", options: { filter: false, sort: false } },
    { label: "End Date", name: "endDate", options: { filter: false, sort: false } },
    { label: "P.O. Amount", name: "total", options: { filter: false, sort: false } },
    { label: "Status", name: "status", options: { filter: true, sort: true } },
    { label: "Received", name: "received", options: { filter: true, sort: true } }
  ];

  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsSelected,
    rowsExpanded,
    onRowSelectionChange,
    onRowExpansionChange,
    customToolbarSelect,
    selectableRows: 'single',
    expandableRows: true,
    expandableRowsHeader: false,
    expandableRowsOnClick: false,
    isRowExpandable: (dataIndex, expandedRows) => {
      // Prevent expand/collapse of any row if there are 3 rows expanded already (but allow those already expanded to be collapsed)
      if (expandedRows.data.length > 3 && expandedRows.data.filter(d => d.dataIndex === dataIndex).length === 0)
        return false;
      return true;
    },
    renderExpandableRow: (rowData, rowMeta) => {
      const colSpan = rowData.length + 1;
      const purchaseOrderId = rowData[0];
      const rowProducts = tableData.find(purchaseOrder => purchaseOrder.id === purchaseOrderId).products;
      return (
        <tr>
          <td colSpan={colSpan}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product ID</TableCell>
                    <TableCell>Product Name</TableCell>
                    <TableCell>Product Brand</TableCell>
                    <TableCell>Unit Cost</TableCell>
                    <TableCell>Total Quantity</TableCell>
                    <TableCell>Received Quantity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowProducts.map((rowProduct) => {
                    const productInfo = props.products.find(product => product.id === rowProduct.productId);
                    return (
                      <TableRow key={productInfo ? productInfo.displayId : Math.floor(Math.random() * 1000).toString()}>
                        <TableCell>
                          {productInfo ? productInfo.displayId : ""}
                        </TableCell>
                        <TableCell>{productInfo ? productInfo.name : ""}</TableCell>
                        <TableCell>{productInfo ? productInfo.company : ""}</TableCell>
                        <TableCell>{productInfo ? productInfo.unitCost : ""}</TableCell>
                        <TableCell>{rowProduct.totalQuantity}</TableCell>
                        <TableCell>{rowProduct.receivedQuantity}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </td>
        </tr>
      );
    }
  };
  
  const handleReceiveOrPaymentClose = (closeProducts) => {
    if(closeProducts) setOpenReceiveModal(false);
    else setOpenPaymentModal(false);
    
    setRowsSelected([]);
    props.clearNewPO();
  };
  
  const handleCancelModalOpen = () => {
    setOpenCancelModal(true);
  };

  const handleCancelModalClose = () => {
    setOpenCancelModal(false);
    setRowsSelected([]);
  };
  
  const cancelPurchaseOrder = (purchaseOrderId) => {
    props.deletePurchaseOrder(purchaseOrderId);

    setOpenCancelModal(false);
    setRowsSelected([]);
  };

  const renderCancelModal = () => {
    const purchaseOrderInfo = tableData[rowsSelected[0]];

    return (
      <Modal
        open={openCancelModal}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20%'
        }}
      >
        <Card>
          <CardHeader
            title="Cancel Purchase Order ?"
          />
          <Divider />
          <CardContent>
            <p> The Following Purchase Order will be Cancelled... </p>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Button
                color="secondary"
                variant="contained"
                onClick={cancelPurchaseOrder.bind(this, purchaseOrderInfo.id)}
              >
                Cancel
              </Button>
              <Button sx={{ mx: 1 }} onClick={handleCancelModalClose}>
                Close
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Modal>
    )
  };

  return (
  <React.Fragment>
    <MUIDataTable 
      title={"Purchase Orders"}
      data={tableData}
      columns={columns}
      options={options}
    />
    { openReceiveModal ?
    <ReceiveProductsModal
      open={openReceiveModal}
      purchaseOrders={props.purchaseOrders}
      closeModal={handleReceiveOrPaymentClose}
    /> : null }
    { openPaymentModal ?
    <PurchaseOrderPaymentModal
      open={openPaymentModal}
      purchaseOrders={props.purchaseOrders}
      vendors={props.vendors}
      user={props.user}
      closeModal={handleReceiveOrPaymentClose}
    /> : null }
    { openCancelModal ? renderCancelModal() : null }
  </React.Fragment>
  )
};

export default connect (null, {updatePurchaseOrderStatus, deletePurchaseOrder, setNewPOStatus, setNewPOInfo, clearNewPO}) (PurchaseOrdersTable);